import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatDate, formatDateTime, daysUntil } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2, Clock, CircleDot, Bell, Flame, AlertTriangle, CircleDashed, CircleCheck, ChevronUp, ChevronDown, Pencil } from "lucide-react";

const PRIORITIES = [
  { value: "urgent", label: "Urgent", cls: "pill-urgent", icon: Flame },
  { value: "high", label: "High", cls: "pill-high", icon: AlertTriangle },
  { value: "normal", label: "Normal", cls: "pill-normal", icon: CircleCheck },
  { value: "low", label: "Low", cls: "pill-low", icon: CircleDashed },
];
const P_MAP = Object.fromEntries(PRIORITIES.map((p) => [p.value, p]));

const STATUS_MAP = {
  open: { label: "Open", icon: CircleDot, cls: "text-muted-foreground" },
  in_progress: { label: "In progress", icon: Clock, cls: "text-[#9A3412]" },
  done: { label: "Done", icon: CheckCircle2, cls: "text-emerald-700" },
};

export default function Todo() {
  const { user } = useAuth();
  const [tab, setTab] = useState("mine");
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editTask, setEditTask] = useState(null); // Task being edited
  const [form, setForm] = useState({
    title: "",
    description: "",
    priority: "normal",
    assignee_id: user?.id || "",
    due_date: "",
    reminder_at: "",
  });

  const fetchTasks = async (scope) => {
    try {
      const { data } = await api.get("/tasks", { params: { scope } });
      setItems(data.items);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    // Clear items immediately when tab changes to prevent showing stale data
    setItems([]);
    fetchTasks(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    // Fetch all users for assignee dropdown - all users can assign to anyone
    api
      .get("/tasks/users")
      .then((r) => setUsers(r.data.items || []))
      .catch(() => setUsers(user ? [user] : []));
  }, [user]);

  useEffect(() => {
    if (user?.id) setForm((f) => ({ ...f, assignee_id: f.assignee_id || user.id }));
  }, [user]);

  const resetForm = () => {
    setForm({ title: "", description: "", priority: "normal", assignee_id: user?.id || "", due_date: "", reminder_at: "" });
    setEditTask(null);
  };

  const openNewTask = () => {
    resetForm();
    setOpen(true);
  };

  const openEditTask = (task) => {
    setEditTask(task);
    setForm({
      title: task.title || "",
      description: task.description || "",
      priority: task.priority || "normal",
      assignee_id: task.assignee_id || "",
      due_date: task.due_date || "",
      reminder_at: task.reminder_at ? task.reminder_at.slice(0, 16) : "", // Format for datetime-local
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.due_date) delete body.due_date;
      if (!body.reminder_at) delete body.reminder_at;
      if (!body.assignee_id) delete body.assignee_id;
      
      if (editTask) {
        // Update existing task
        await api.put(`/tasks/${editTask.id}`, body);
        toast.success("Task updated");
      } else {
        // Create new task
        await api.post("/tasks", body);
        toast.success("Task created");
      }
      setOpen(false);
      resetForm();
      fetchTasks(tab);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/tasks/${id}`, { status });
      fetchTasks(tab);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const updateAssignee = async (id, assignee_id) => {
    try {
      await api.put(`/tasks/${id}`, { assignee_id });
      toast.success("Task reassigned");
      fetchTasks(tab);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/tasks/${id}`);
      fetchTasks(tab);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reorder = async (id, direction) => {
    try {
      await api.post(`/tasks/${id}/reorder`, null, { params: { direction } });
      fetchTasks(tab);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="fade-up" data-testid="todo-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Team</p>
          <h1 className="font-heading text-3xl tracking-tight">To-Do</h1>
          <p className="text-sm text-muted-foreground mt-1">Assign tasks, set reminders and track what's in motion.</p>
        </div>
        <Button onClick={openNewTask} style={{ backgroundColor: "#9E6D5B" }} data-testid="new-task-btn">
          <Plus className="w-4 h-4 mr-2" /> New task
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="todo-tabs">
          <TabsTrigger value="mine" data-testid="todo-tab-mine">Assigned to me</TabsTrigger>
          <TabsTrigger value="created" data-testid="todo-tab-created">Created by me</TabsTrigger>
          <TabsTrigger value="all" data-testid="todo-tab-all">All</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <TaskTable 
            items={items} 
            users={users}
            onStatus={updateStatus} 
            onAssignee={updateAssignee}
            onDelete={remove} 
            onReorder={reorder} 
            onEdit={openEditTask}
            allowReorder={tab === "mine"} 
            currentTab={tab} 
          />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
        <DialogContent data-testid="task-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">{editTask ? "Edit task" : "New task"}</DialogTitle>
            <DialogDescription>
              {editTask ? "Update task details, assignee, or due date." : "Assign to a teammate with an optional due date and reminder."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                className="mt-1"
                data-testid="task-title-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
              <Textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-1"
                data-testid="task-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Assignee</Label>
                <Select value={form.assignee_id || ""} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="task-assignee-select">
                    <SelectValue placeholder="Select assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id} data-testid={`task-assignee-opt-${u.id}`}>
                        {u.name} {u.id === user?.id ? "(you)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="mt-1" data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Due date</Label>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  className="mt-1"
                  data-testid="task-due-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Reminder at</Label>
                <Input
                  type="datetime-local"
                  value={form.reminder_at}
                  onChange={(e) => setForm({ ...form, reminder_at: e.target.value })}
                  className="mt-1"
                  data-testid="task-reminder-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setOpen(false); resetForm(); }} data-testid="task-cancel-btn">Cancel</Button>
              <Button type="submit" disabled={saving} style={{ backgroundColor: "#9E6D5B" }} data-testid="task-submit-btn">
                {saving ? "Saving…" : editTask ? "Update task" : "Create task"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaskTable({ items, users, onStatus, onAssignee, onDelete, onReorder, onEdit, allowReorder, currentTab }) {
  if (!items.length) {
    return (
      <Card className="p-10 text-center text-sm text-muted-foreground">
        No tasks in this view yet. Click <b>New task</b> to start one.
      </Card>
    );
  }
  // Visually: open/in_progress first (keep backend order), completed at bottom.
  const openTasks = items.filter((t) => t.status !== "done");
  const doneTasks = items.filter((t) => t.status === "done");
  const sorted = [...openTasks, ...doneTasks];
  
  // In "mine" tab, show who assigned the task (creator). Otherwise show assignee with dropdown.
  const showAssigner = currentTab === "mine";
  const personColumnLabel = showAssigner ? "Assigned by" : "Assignee";
  
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--muted))]">
            <tr className="text-left">
              <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium w-14">#</th>
              <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Task</th>
              <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{personColumnLabel}</th>
              <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Priority</th>
              <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Due</th>
              <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Status</th>
              <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((t, visualIdx) => {
              const P = P_MAP[t.priority] || P_MAP.normal;
              const S = STATUS_MAP[t.status] || STATUS_MAP.open;
              const d = daysUntil(t.due_date);
              const overdue = d !== null && d < 0 && t.status !== "done";
              const isOpen = t.status !== "done";
              const isFirstDone = t.status === "done" && (sorted[visualIdx - 1]?.status !== "done");
              const openIdx = isOpen ? openTasks.findIndex((x) => x.id === t.id) : -1;
              const canUp = allowReorder && isOpen && openIdx > 0;
              const canDown = allowReorder && isOpen && openIdx >= 0 && openIdx < openTasks.length - 1;
              
              return (
                <React.Fragment key={t.id}>
                  {isFirstDone && (
                    <tr>
                      <td colSpan={7} className="p-2 text-[10px] uppercase tracking-widest text-muted-foreground bg-[hsl(var(--muted))]/60">
                        Completed
                      </td>
                    </tr>
                  )}
                  <tr className={`data-row border-t border-[hsl(var(--border))] ${t.status === "done" ? "opacity-60" : ""}`} data-testid={`task-row-${t.id}`}>
                    <td className="p-2 align-middle">
                      {allowReorder && isOpen ? (
                        <div className="flex flex-col items-center gap-0.5">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6"
                            onClick={() => onReorder(t.id, "up")}
                            disabled={!canUp}
                            data-testid={`task-up-btn-${t.id}`}
                            title="Move up"
                          >
                            <ChevronUp className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6"
                            onClick={() => onReorder(t.id, "down")}
                            disabled={!canDown}
                            data-testid={`task-down-btn-${t.id}`}
                            title="Move down"
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground font-mono-data">{isOpen ? openIdx + 1 : ""}</span>
                      )}
                    </td>
                    <td className="p-3 max-w-[320px]">
                      <div 
                        className="font-medium truncate cursor-pointer hover:text-[#9E6D5B] transition-colors" 
                        title={`${t.title} (click to edit)`}
                        onClick={() => onEdit(t)}
                        data-testid={`task-title-${t.id}`}
                      >
                        {t.title}
                      </div>
                      {t.description && <div className="text-xs text-muted-foreground mt-0.5 truncate">{t.description}</div>}
                      {t.reminder_at && (
                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Bell className="w-3 h-3" /> Reminder {formatDateTime(t.reminder_at)}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-sm">
                      {showAssigner ? (
                        // Show creator name (who assigned it to me)
                        <span>{t.creator_name || "—"}</span>
                      ) : (
                        // Show assignee dropdown for reassignment
                        <Select 
                          value={t.assignee_id || ""} 
                          onValueChange={(v) => onAssignee(t.id, v)}
                        >
                          <SelectTrigger 
                            className="w-[140px] h-8" 
                            data-testid={`task-assignee-select-${t.id}`}
                          >
                            <SelectValue placeholder="Unassigned" />
                          </SelectTrigger>
                          <SelectContent>
                            {users.map((u) => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`pill ${P.cls}`}><P.icon className="w-3 h-3" /> {P.label}</span>
                    </td>
                    <td className="p-3 text-xs font-mono-data">
                      {t.due_date ? (
                        <>
                          {formatDate(t.due_date)}
                          {d !== null && (
                            <span className={`ml-2 ${overdue ? "text-destructive" : "text-muted-foreground"}`}>
                              {overdue ? `${Math.abs(d)}d overdue` : d === 0 ? "today" : `${d}d`}
                            </span>
                          )}
                        </>
                      ) : "—"}
                    </td>
                    <td className="p-3">
                      <Select value={t.status} onValueChange={(v) => onStatus(t.id, v)}>
                        <SelectTrigger className="w-[140px] h-8" data-testid={`task-status-select-${t.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_MAP).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className={`inline-flex items-center gap-1 text-xs mt-1 ${S.cls}`}>
                        <S.icon className="w-3 h-3" /> {S.label}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => onEdit(t)} 
                          data-testid={`task-edit-btn-${t.id}`}
                          title="Edit task"
                        >
                          <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => onDelete(t.id)} data-testid={`task-delete-btn-${t.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
